import { doc, getDoc } from 'firebase/firestore';
import { firestore } from './firebase';
import { cacheService } from './cacheService';

/**
 * Get current season information from Firestore with caching
 * @returns {Object|null} Current season information or null if not found
 */
export const getCurrentSeasonInfo = async () => {
  try {
    // Check cache first
    const cached = cacheService.getSeasonConfig('current');
    if (cached) {
      // Background refresh for next time
      setTimeout(async () => {
        try {
          const seasonDocRef = doc(firestore, 'seasons', 'current');
          const seasonDoc = await getDoc(seasonDocRef);
          if (seasonDoc.exists()) {
            cacheService.setSeasonConfig('current', seasonDoc.data());
          }
        } catch (error) {
          console.warn('Background season refresh failed:', error);
        }
      }, 100);
      
      return cached;
    }

    // Load from Firestore if no cache
    const seasonDocRef = doc(firestore, 'seasons', 'current');
    const seasonDoc = await getDoc(seasonDocRef);
    
    if (seasonDoc.exists()) {
      const seasonData = seasonDoc.data();
      // Cache for 12 hours (season config changes rarely)
      cacheService.setSeasonConfig('current', seasonData, 12 * 60 * 60 * 1000);
      return seasonData;
    }
    return null;
  } catch (error) {
    console.error('Error getting current season info:', error);
    return null;
  }
};

/**
 * Check if the current date is within the active Premier League season
 * @param {Object} seasonInfo - Season information object (optional, will fetch if not provided)
 * @returns {boolean} True if season is currently active
 */
export const isSeasonActive = async (seasonInfo = null) => {
  try {
    if (!seasonInfo) {
      seasonInfo = await getCurrentSeasonInfo();
    }
    
    if (!seasonInfo) {
      console.log('No season information available, assuming season is inactive');
      return false;
    }
    
    const now = new Date();
    const startDate = new Date(seasonInfo.startDate);
    const endDate = new Date(seasonInfo.endDate);
    
    // Add one day buffer after end date to allow final updates
    const endDateWithBuffer = new Date(endDate);
    endDateWithBuffer.setDate(endDateWithBuffer.getDate() + 1);
    
    const isActive = now >= startDate && now <= endDateWithBuffer;
    
    return isActive;
  } catch (error) {
    console.error('Error checking if season is active:', error);
    return false; // Default to inactive on error
  }
};

/**
 * Get season year information
 * @param {Object} seasonInfo - Season information object (optional, will fetch if not provided)
 * @returns {Object} Object containing seasonYear, nextYear, and seasonYearString
 */
export const getSeasonYearInfo = async (seasonInfo = null) => {
  try {
    if (!seasonInfo) {
      seasonInfo = await getCurrentSeasonInfo();
    }
    
    if (!seasonInfo) {
      // Fallback to current year logic if no season info
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      // If it's before August, we're in the previous season
      const seasonStartYear = currentMonth < 7 ? currentYear - 1 : currentYear;
      return {
        seasonYear: seasonStartYear,
        nextYear: seasonStartYear + 1,
        seasonYearString: `${seasonStartYear}-${seasonStartYear + 1}`
      };
    }
    
    return {
      seasonYear: seasonInfo.year,
      nextYear: seasonInfo.nextYear,
      seasonYearString: `${seasonInfo.year}-${seasonInfo.nextYear}`
    };
  } catch (error) {
    console.error('Error getting season year info:', error);
    // Fallback
    const currentYear = new Date().getFullYear();
    return {
      seasonYear: currentYear,
      nextYear: currentYear + 1,
      seasonYearString: `${currentYear}-${currentYear + 1}`
    };
  }
};

/**
 * Get formatted season status text
 * @param {Object} seasonInfo - Season information object (optional, will fetch if not provided)
 * @returns {string} Formatted season status
 */
export const getSeasonStatusText = async (seasonInfo = null) => {
  try {
    if (!seasonInfo) {
      seasonInfo = await getCurrentSeasonInfo();
    }
    
    if (!seasonInfo) {
      return 'Season information unavailable';
    }
    
    const isActive = await isSeasonActive(seasonInfo);
    // startDate/endDate are date-only strings (e.g. "2026-05-24"), which JS parses as
    // midnight UTC. Format them in UTC so they don't display a day earlier for viewers
    // in negative-UTC timezones.
    const startDate = new Date(seasonInfo.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' });
    const endDate = new Date(seasonInfo.endDate).toLocaleDateString(undefined, { timeZone: 'UTC' });
    
    if (isActive) {
      return `Season ${seasonInfo.year}-${seasonInfo.nextYear} Active (${startDate} - ${endDate})`;
    } else {
      const now = new Date();
      const start = new Date(seasonInfo.startDate);
      
      if (now < start) {
        return `Season ${seasonInfo.year}-${seasonInfo.nextYear} Starts ${startDate}`;
      } else {
        return `Season ${seasonInfo.year}-${seasonInfo.nextYear} Ended ${endDate}`;
      }
    }
  } catch (error) {
    console.error('Error getting season status text:', error);
    return 'Season status unavailable';
  }
};